import {
  Alert,
  AlertIcon,
  Box,
  Button as ChakraButton,
  Divider,
  Flex,
  Heading,
  Icon,
  Text,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import { buildDayjs } from "@metriport/shared/common/date";
import { useEffect, useState } from "react";
import { FaArrowRight, FaDownload, FaExternalLinkAlt } from "react-icons/fa";
import { TcmEncounter } from "../../../api/tcm-encounter";
import { EhrName } from "../../../domain/ehr";
import { formatDateTime } from "../../shared/date";
import { Button, METRIPORT_PRIMARY, METRIPORT_PRIMARY_HOVER_BG } from "../shared-components/button";
import { DetailsDisplay } from "../shared-components/fhir-sections/shared/details";
import { useDocumentMenu } from "../shared-logic/useDocumentMenu";
import { OutreachAction } from "../transitions-of-care/outreach-actions";
import { ClinicalInformationDisplay } from "./clinical-information-display";
import { OutreachLogs } from "./outreach-logs";

export type EncounterDetailsProps = {
  encounter: TcmEncounter;
  onNoteChange: (note: string) => void;
  onSummaryFilePathChange: (filePath: string) => void;
  onOutreachLogUpdate: (status: "Attempted" | "Completed", action: OutreachAction) => void;
  onNavigateToPatient?: (patientId: string) => void;
  getPatientUrl?: (patientId: string) => string;
  isImpersonating?: boolean;
};

export function EncounterDetails({
  encounter,
  onNoteChange,
  onSummaryFilePathChange,
  onOutreachLogUpdate,
  onNavigateToPatient,
  getPatientUrl,
  isImpersonating = false,
}: EncounterDetailsProps) {
  const [noteText, setNoteText] = useState(encounter.freetextNote ?? "");
  const [summaryFilePath, setsummaryFilePath] = useState(encounter.dischargeSummaryPath ?? "");
  const { handleDocumentClick, documentMenuComponent } = useDocumentMenu();

  const dividerBorderColor = useColorModeValue("gray.300", "gray.600");
  const headingColor = useColorModeValue("gray.800", "white");

  // v0 just for eclinicalworks
  const ecwExternalUrl = encounter.externalUrls?.find(
    url => url.source === EhrName.EClinicalWorks.toLowerCase()
  )?.url;

  // Sync local state when encounter prop changes
  useEffect(() => {
    setNoteText(encounter.freetextNote ?? "");
  }, [encounter.freetextNote]);

  useEffect(() => {
    setsummaryFilePath(encounter.dischargeSummaryPath ?? "");
  }, [encounter.dischargeSummaryPath]);

  const filePath = encounter.dischargeSummaryPath;
  const isNotFound =
    !filePath && buildDayjs(encounter.createdAt).add(8, "day").isBefore(buildDayjs());

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!filePath) return;

    const syntheticPointerEvent = {
      clientX: event.clientX,
      clientY: event.clientY,
    } as React.PointerEvent<HTMLElement>;

    handleDocumentClick(
      {
        id: encounter.id,
        fileName: filePath,
        fileType: "xml",
      },
      syntheticPointerEvent
    );
  };

  // Prepare details object for DetailsDisplay
  const encounterDetails: { [key: string]: string | undefined } = {
    Location: encounter.facilityName ?? "Not specified",
    Class: encounter.class ?? "Not specified",
    "Admit Date": formatDateTime(encounter.admitTime),
    "Discharge Date": formatDateTime(encounter.dischargeTime),
  };

  // Add phone numbers if they exist
  if (encounter.patientPhoneNumbers && encounter.patientPhoneNumbers.length > 0) {
    const phoneLabel = encounter.patientPhoneNumbers.length > 1 ? "Phone Numbers" : "Phone Number";
    encounterDetails[phoneLabel] = encounter.patientPhoneNumbers.join(", ");
  }

  return (
    <Box px={4} display="flex" flexDirection="column" overflow="hidden" height="100%" width="100%">
      {documentMenuComponent}
      <Flex direction="column" height="100%" justifyContent="flex-start">
        <Flex alignItems="center" justifyContent="space-between">
          <Flex direction="column" gap={1}>
            <Heading size="md" color={useColorModeValue("gray.800", "white")}>
              <Text as="span" color={useColorModeValue("blue.700", "blue.200")}>
                {encounter.patientFirstName} {encounter.patientLastName}
              </Text>
            </Heading>
            <Flex direction="column" gap={1}>
              <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.300")}>
                DOB: {encounter.patientDateOfBirth || "Not specified"}
              </Text>
            </Flex>
          </Flex>
          <Flex gap={2}>
            {onNavigateToPatient && getPatientUrl && (
              <ChakraButton
                as="a"
                href={getPatientUrl(encounter.patientId)}
                bg={METRIPORT_PRIMARY}
                color="white"
                _hover={{ bg: METRIPORT_PRIMARY_HOVER_BG }}
                rightIcon={<Icon as={FaArrowRight} fontSize="xs" />}
                size="sm"
                onClick={e => {
                  e.preventDefault();
                  onNavigateToPatient(encounter.patientId);
                }}
              >
                See Patient
              </ChakraButton>
            )}
            {onNavigateToPatient && !getPatientUrl && (
              <ChakraButton
                bg={METRIPORT_PRIMARY}
                color="white"
                _hover={{ bg: METRIPORT_PRIMARY_HOVER_BG }}
                rightIcon={<Icon as={FaArrowRight} fontSize="xs" />}
                size="sm"
                onClick={() => onNavigateToPatient(encounter.patientId)}
              >
                See Patient
              </ChakraButton>
            )}
            {ecwExternalUrl && (
              <Button
                rightIcon={<Icon as={FaExternalLinkAlt} fontSize="xs" />}
                size="sm"
                onClick={() => window.open(ecwExternalUrl, "_blank")}
              >
                View in EHR
              </Button>
            )}
          </Flex>
        </Flex>
        <Divider mt={4} borderColor={useColorModeValue("gray.300", "gray.600")} />
        <Box pt={4} overflow="auto">
          <DetailsDisplay details={encounterDetails} />
          <ClinicalInformationDisplay encounter={encounter} />

          {/* Discharge summary download / status */}
          <Heading size="md" color={useColorModeValue("gray.800", "white")} mt={4} mb={2}>
            Discharge Summary
          </Heading>
          <Flex alignItems="center" justifyContent="space-between" mb={4}>
            {filePath ? (
              <Alert status="success" mt={4}>
                <Flex alignItems="center" justifyContent="space-between" width="100%">
                  <Flex alignItems="center">
                    <AlertIcon />
                    Discharge summary found
                  </Flex>
                  <Button
                    rightIcon={<Icon as={FaDownload} fontSize="xs" />}
                    size="sm"
                    onClick={handleDownloadClick}
                  >
                    Download
                  </Button>
                </Flex>
              </Alert>
            ) : isNotFound ? (
              <Alert status="warning" mt={4}>
                <AlertIcon />
                Discharge summary not found
              </Alert>
            ) : (
              <Alert status="info" mt={4}>
                <AlertIcon />
                Discharge summary lookup is in progress...
              </Alert>
            )}
          </Flex>

          <Divider my={4} borderColor={dividerBorderColor} />
          <OutreachLogs encounter={encounter} onOutreachLogUpdate={onOutreachLogUpdate} />

          <Divider my={4} borderColor={useColorModeValue("gray.300", "gray.600")} />
          <Heading size="md" color={useColorModeValue("gray.800", "white")} mt={4} mb={2}>
            Notes
          </Heading>
          <Box position="relative">
            <Textarea
              w="full"
              minH="180px"
              placeholder="Add notes..."
              value={noteText}
              onChange={e => {
                setNoteText(e.target.value);
                onNoteChange(e.target.value);
              }}
              sx={{
                "&:focus": {
                  "& + button": {
                    zIndex: 2,
                  },
                },
              }}
            />
          </Box>

          {/* Manual discharge to encounter association */}
          {isImpersonating && (
            <Box>
              <Divider my={4} borderColor={dividerBorderColor} />
              <Heading size="md" color={headingColor} mt={4} mb={2}>
                Discharge Summary File Path
              </Heading>
              <Box position="relative">
                <Textarea
                  w="full"
                  minH="180px"
                  placeholder="Insert the s3 title of the discharge summary document, i.e. cxId/patientId/cxId_patientId_docId.xml"
                  value={summaryFilePath}
                  onChange={e => {
                    setsummaryFilePath(e.target.value);
                  }}
                  sx={{
                    "&:focus": {
                      "& + button": {
                        zIndex: 2,
                      },
                    },
                  }}
                />
                <Button
                  position="absolute"
                  bottom={2}
                  right={2}
                  size="sm"
                  colorScheme="gray"
                  borderRadius="md"
                  _hover={{
                    transform: "translateY(-1px)",
                    bg: "gray.200",
                  }}
                  transition="transform 0.2s"
                  zIndex={1}
                  onClick={() => onSummaryFilePathChange(summaryFilePath)}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}
          <Divider my={4} borderColor={useColorModeValue("gray.300", "gray.600")} />

          {/* Patient ID and Encounter ID at the bottom */}
          <Flex direction="column" mt="auto" alignItems="flex-end" gap={0}>
            <Text
              fontSize="xs"
              color={useColorModeValue("gray.600", "gray.300")}
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              Patient {encounter.patientId}
            </Text>
            <Text
              fontSize="xs"
              color={useColorModeValue("gray.600", "gray.300")}
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              Encounter {encounter.id}
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
